/**
 * Public Badge usage page — `/badge`
 * ===================================
 * Shows the README badge variants and gives one-click copy buttons
 * for Markdown, HTML and reStructuredText.
 *
 * Each adopted badge is a free ad + an SEO backlink → viral marketing flywheel.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge as Pill } from '../components/ui/badge';
import {
  ArrowLeft, Sparkles, Github, Copy, ShieldCheck, Heart,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

// Snippets always point to the canonical production URL so README adopters
// link to cartieai.com regardless of where they copied them from. The visual
// preview uses the current backend URL so the page works on every environment.
const PROD = 'https://cartieai.com';
const PREVIEW_HOST = `${process.env.REACT_APP_BACKEND_URL}`;

const VARIANTS = [
  { key: 'default',   label: 'Cost-aware ✓',         color: 'brightgreen', text: 'Recommended for OSS repos and teams that opt in publicly to cost discipline.' },
  { key: 'finops',    label: 'FinOps practitioner',   color: 'green',        text: 'For teams who run an active FinOps practice.' },
  { key: 'audit',     label: 'cost-audit yearly',     color: 'blue',         text: 'For teams who run an annual cost audit (often with us).' },
  { key: 'gpu',       label: 'GPU-budget aware',      color: 'violet',       text: 'For ML/AI teams who track GPU-hour budgets at PR time.' },
];

const buildBadgeUrl = (label, value, color, host = PROD) => {
  const params = new URLSearchParams({ label, value, color });
  return `${host}/api/badge.svg?${params.toString()}`;
};

const buildMarkdown = (label, value, color) =>
  `[![${label}: ${value}](${buildBadgeUrl(label, value, color)})](${PROD}/trust)`;

const buildHtml = (label, value, color) =>
  `<a href="${PROD}/trust"><img alt="${label}: ${value}" src="${buildBadgeUrl(label, value, color)}" /></a>`;

const buildRst = (label, value, color) =>
  `.. image:: ${buildBadgeUrl(label, value, color)}\n   :alt: ${label}: ${value}\n   :target: ${PROD}/trust`;

const BadgePage = () => {
  const [copiedKey, setCopiedKey] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="badge-page">
      <nav className="fixed top-0 w-full z-50 bg-slate-900 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Pill className="mb-3 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 tracking-wider">
              <Github className="w-3 h-3 mr-1" />FREE FOR EVERY REPO
            </Pill>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-3"><BrandTitle>Wear it on your README.</BrandTitle></h1>
            <p className="text-slate-300 max-w-2xl text-base sm:text-lg leading-relaxed">
              A small, honest signal that your team takes cloud cost seriously. Drop one line into your <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded text-emerald-300">README.md</code> and you're done. The badge points to our public Trust page so anyone clicking gets the same security &amp; cost-stance proof we publish for InfoSec reviews. <strong className="text-white">Free, forever, no signup.</strong>
            </p>
          </div>

          {/* Live preview row */}
          <Card className="mb-6 bg-slate-900/60 border-emerald-500/20" data-testid="preview-card">
            <CardHeader>
              <CardTitle className="text-base text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-400" />Pick a variant
              </CardTitle>
              <CardDescription>Click any badge to copy the Markdown.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {VARIANTS.map((v) => (
                <div key={v.key} className="grid sm:grid-cols-[auto,1fr,auto] items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                  <img
                    src={buildBadgeUrl('CARTIEAI', v.label, v.color, PREVIEW_HOST)}
                    alt={`CARTIEAI: ${v.label}`}
                    height={20}
                    className="cursor-pointer"
                    onClick={() => copy(buildMarkdown('CARTIEAI', v.label, v.color), `md-${v.key}`)}
                    data-testid={`badge-preview-${v.key}`}
                  />
                  <p className="text-xs text-slate-400">{v.text}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                    onClick={() => copy(buildMarkdown('CARTIEAI', v.label, v.color), `md-${v.key}`)}
                    data-testid={`copy-md-${v.key}`}
                  >
                    {copiedKey === `md-${v.key}` ? <>Copied!</> : <><Copy className="h-3 w-3 mr-1.5" />Markdown</>}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Snippets */}
          <Card className="mb-6 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-base text-white">Copy-paste snippets</CardTitle>
              <CardDescription>Default variant. Swap label / value / color in the URL to customise.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Snippet
                title="Markdown (README.md)"
                code={buildMarkdown('CARTIEAI', 'Cost-aware ✓', 'brightgreen')}
                onCopy={() => copy(buildMarkdown('CARTIEAI', 'Cost-aware ✓', 'brightgreen'), 'md-default')}
                copied={copiedKey === 'md-default'}
                testId="snippet-md"
              />
              <Snippet
                title="HTML"
                code={buildHtml('CARTIEAI', 'Cost-aware ✓', 'brightgreen')}
                onCopy={() => copy(buildHtml('CARTIEAI', 'Cost-aware ✓', 'brightgreen'), 'html')}
                copied={copiedKey === 'html'}
                testId="snippet-html"
              />
              <Snippet
                title="reStructuredText (Sphinx, PyPI)"
                code={buildRst('CARTIEAI', 'Cost-aware ✓', 'brightgreen')}
                onCopy={() => copy(buildRst('CARTIEAI', 'Cost-aware ✓', 'brightgreen'), 'rst')}
                copied={copiedKey === 'rst'}
                testId="snippet-rst"
              />
            </CardContent>
          </Card>

          {/* What clicking the badge does */}
          <Card className="mb-6 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-base text-white flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-emerald-400" />What clicking the badge shows
              </CardTitle>
              <CardDescription>The badge links to our public Trust page — not a paywall.</CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-slate-300 space-y-2">
              <p>• Live security checks (TLS, security headers, tenant isolation)</p>
              <p>• Subprocessor list (email-gated for InfoSec reviewers)</p>
              <p>• Vendor due-diligence pack download</p>
              <p>• The truthful "SOC 2-aligned today, formal Type II at customer #25" framing</p>
              <p className="text-xs text-slate-400 italic pt-2 border-t border-white/5">No tracking on clicks beyond standard server-access logs. No cookies set on click.</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/30">
            <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Heart className="h-5 w-5 text-emerald-400 shrink-0" />
                <p className="text-sm text-white">
                  <strong>Show us your README!</strong> Email <a href="mailto:hello@cartieai.com" className="text-emerald-300 hover:underline">hello@cartieai.com</a> with a link and we'll boost it on Twitter.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <footer className="py-6 bg-slate-950 border-t border-white/5 text-center text-xs text-slate-400">
        © 2026 CARTIEAI · <Link to="/privacy-policy" className="hover:text-slate-300">Privacy</Link> · <Link to="/terms" className="hover:text-slate-300">Terms</Link>
      </footer>
    </div>
  );
};

const Snippet = ({ title, code, onCopy, copied, testId }) => (
  <div>
    <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">{title}</p>
    <div className="relative">
      <pre className="bg-slate-950 border border-white/5 rounded-md p-3 text-[11.5px] leading-relaxed text-emerald-300 overflow-x-auto pr-20" data-testid={testId}>
{code}
      </pre>
      <Button
        size="sm"
        className="absolute top-2 right-2 bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30"
        onClick={onCopy}
      >
        {copied ? 'Copied!' : <><Copy className="h-3 w-3 mr-1" />Copy</>}
      </Button>
    </div>
  </div>
);

export default BadgePage;
